import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Button, Image } from 'react-bootstrap';
import { Hammer, Users, Puzzle } from 'lucide-react';
import Menu from '../sections/Menu';
import Footer from '../sections/Footer';
import FAQ from '../sections/FAQ';
import Reviews from '../sections/Reviews';
import FeaturesSection from '../sections/FeaturesSection';
import AnimatedCounter from '../components/AnimatedCounter';
import hero_app_vid from '../videos/hero_app_vid.mp4';
import square_hero_vid from '../videos/square_hero_vid.mp4';
import { AnalyticsHandle } from '../components/AnalyticsHandle';
import PageMeta from '../seo/PageMeta';
import { buildHomeStructuredData, buildVideoObjectStructuredData } from '../seo/structuredData';

function Home() {
  const { logRegisterClick } = AnalyticsHandle();
  const [seoContent, setSeoContent] = useState(null);

  useEffect(() => {
    fetch('/data/content.json')
      .then((res) => res.json())
      .then((data) => setSeoContent(data))
      .catch((error) => console.error('Error loading SEO JSON:', error));
  }, []);

  const structuredData = useMemo(
    () =>
      [
        ...buildHomeStructuredData(seoContent),
        buildVideoObjectStructuredData({
          path: "/",
          name: "eGodziny - prezentacja aplikacji",
          description:
            "Przegląd działania aplikacji eGodziny do ewidencji czasu pracy.",
          contentUrl: hero_app_vid,
        }),
        buildVideoObjectStructuredData({
          path: "/",
          name: "eGodziny - funkcje systemu",
          description:
            "Prezentacja funkcji systemu eGodziny dla firm terenowych i agencji.",
          contentUrl: square_hero_vid,
        }),
      ].filter(Boolean),
    [seoContent]
  );

  const handleRegisterClick = () => {
    logRegisterClick('home_hero_primary');
    window.open('/register/1', '_self');
  };

  return (
    <>
      <PageMeta path="/" structuredData={structuredData} />
      <Menu />

      {/* HERO Section */}
      <Container className="py-5" style={{ paddingTop: '80px', paddingBottom: '80px', minHeight: '85vh' }}>
        <Row className="justify-content-center">
          <Col xs={12} lg={10} xl={9}>
            <div className="text-center" style={{ marginBottom: '60px' }}>
              <h1 style={{
                fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
                fontWeight: '800',
                color: '#1f2937',
                marginBottom: '16px',
                lineHeight: '1.15',
                letterSpacing: '-0.02em'
              }}>
                Zero papieru, zero błędów,<br />pełna automatyzacja.
              </h1>

              <p style={{
                fontSize: 'clamp(1.1rem, 2vw, 1.35rem)',
                color: '#4b5563',
                marginBottom: '32px',
                lineHeight: '1.6',
                fontWeight: '500',
                maxWidth: '650px',
                margin: '0 auto 32px auto'
              }}>
                Ewidencja czasu pracy i kontrola obecności w aplikacji.
              </p>

              <div className="d-flex gap-3 justify-content-center flex-wrap hero-buttons" style={{ marginBottom: '24px' }}>
                <Button
                  onClick={handleRegisterClick}
                  style={{
                    backgroundColor: '#407EF9',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '16px 32px',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 14px rgba(64, 126, 249, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(64, 126, 249, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 14px rgba(64, 126, 249, 0.3)';
                  }}
                >
                  Wypróbuj za darmo
                </Button>

                <Button
                  onClick={() => window.open('https://calendly.com/krzysztof-warda-egodziny/30min', '_blank')}
                  style={{
                    backgroundColor: 'white',
                    color: '#1f2937',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '16px 32px',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.borderColor = '#407EF9';
                    e.target.style.color = '#407EF9';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.color = '#1f2937';
                  }}
                >
                  Umów bezpłatne demo
                </Button>
              </div>

              {/* Partners logos under CTA */}
              <div className="d-flex justify-content-center partner-logos-wrapper">
                <div className="partner-logos-grid">
                  <img src={require('../images/pg.png')} alt="PG" className="partner-logo" />
                  <img src={require('../images/excento.png')} alt="Excento" className="partner-logo" />
                  <img src={require('../images/sanicom.png')} alt="Sanicom" className="partner-logo" />
                  <img src={require('../images/innova.png')} alt="Innova" className="partner-logo" />
                  <img src={require('../images/petrostal.png')} alt="Petrostal" className="partner-logo" />
                  <img src={require('../images/verisnet.png')} alt="Verisnet" className="partner-logo" />
                </div>
              </div>

              <div className="d-flex justify-content-center" style={{ marginTop: '40px' }}>
                <div
                  className="hero-video-wrapper"
                  style={{
                    width: '75%',
                    maxWidth: '900px',
                    position: 'relative',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.35)',
                    margin: '0 auto'
                  }}
                >
                  <video
                    className="rounded-3"
                    autoPlay
                    loop
                    muted
                    playsInline
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                      borderRadius: '16px'
                    }}
                  >
                    <source src={hero_app_vid} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>

              {/* Features Section under video */}
              <div style={{ marginTop: '48px' }}>
                <FeaturesSection />
              </div>

              {/* Square hero video below Features Section */}
              <div className="d-flex justify-content-center">
                <div
                  className="hero-video-wrapper"
                  style={{
                    width: '75%',
                    maxWidth: '900px',
                    position: 'relative',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.35)',
                    margin: '0 auto'
                  }}
                >
                  <video
                    className="rounded-3"
                    autoPlay
                    loop
                    muted
                    playsInline
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                      borderRadius: '16px'
                    }}
                  >
                    <source src={square_hero_vid} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Testimonials Section */}
      <div style={{ backgroundColor: '#f8f9fa', paddingTop: '60px'}}>
        <Container className="py-1">
          <div className="text-center">
            <h2 style={{
              fontSize: '2.2rem',
              fontWeight: '700',
              color: '#1f2937',
              marginBottom: '16px'
            }}>
              Co mówią nasi klienci?
            </h2>
            <p style={{
              fontSize: '1.1rem',
              color: '#6b7280',
              maxWidth: '700px',
              margin: '0 auto'
            }}>
              Poznaj opinie firm, które zaufały eGodzinom
            </p>
          </div>
          <Reviews />
        </Container>
      </div>

      {/* Segmentacja branżowa */}
      <Container className="py-5" style={{ marginTop: '60px', marginBottom: '60px' }}>
        <div className="text-center" style={{ marginBottom: '48px' }}>
          <h2 style={{
            fontSize: '2.2rem',
            fontWeight: '700',
            color: '#1f2937',
            marginBottom: '16px'
          }}>
            Różne firmy, różne potrzeby.
          </h2>
          <p style={{
            fontSize: '1.1rem',
            color: '#6b7280',
            maxWidth: '700px',
            margin: '0 auto'
          }}>
            Niezależnie od tego, czy prowadzisz firmę inżynierską, czy zarządzasz pracownikami u wielu klientów. Mamy gotowe rozwiązanie dla Twojej branży.
          </p>
        </div>

        <Row className="g-4">
          <Col xs={12} sm={6} md={4}>
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              padding: '32px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.3s ease',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
            }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.12)';
                e.currentTarget.style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}>
              <div style={{ marginBottom: '16px' }}>
                <Hammer size={36} color="#407EF9" strokeWidth={1.5} />
              </div>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#1f2937',
                marginBottom: '12px'
              }}>
                Firmy inżynierskie
              </h3>
              <p style={{
                fontSize: '16px',
                color: '#4b5563',
                marginBottom: '20px',
                lineHeight: '1.6',
                flex: 1
              }}>
                Prosta ewidencja czasu pracy i rozliczenia projektów bez papieru.
              </p>
              <a
                href="/inzynieria-i-budownictwo"
                style={{
                  color: '#407EF9',
                  fontSize: '16px',
                  fontWeight: '500',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.gap = '8px';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.gap = '4px';
                }}
              >
                Zobacz rozwiązanie →
              </a>
            </div>
          </Col>

          <Col xs={12} sm={6} md={4}>
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              padding: '32px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.3s ease',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
            }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.12)';
                e.currentTarget.style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}>
              <div style={{ marginBottom: '16px' }}>
                <Users size={36} color="#407EF9" strokeWidth={1.5} />
              </div>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#1f2937',
                marginBottom: '12px'
              }}>
                Agencje pracy i outsourcing
              </h3>
              <p style={{
                fontSize: '16px',
                color: '#4b5563',
                marginBottom: '20px',
                lineHeight: '1.6',
                flex: 1
              }}>
                Kontrola obecności u wielu klientów i szybkie rozliczenia godzin.
              </p>
              <a
                href="/praca-tymczasowa-i-outsourcing"
                style={{
                  color: '#407EF9',
                  fontSize: '16px',
                  fontWeight: '500',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.gap = '8px';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.gap = '4px';
                }}
              >
                Zobacz rozwiązanie →
              </a>
            </div>
          </Col>

          <Col xs={12} sm={6} md={4}>
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              padding: '32px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.3s ease',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
            }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.12)';
                e.currentTarget.style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}>
              <div style={{ marginBottom: '16px' }}>
                <Puzzle size={36} color="#407EF9" strokeWidth={1.5} />
              </div>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#1f2937',
                marginBottom: '12px'
              }}>
                Inne firmy
              </h3>
              <p style={{
                fontSize: '16px',
                color: '#4b5563',
                marginBottom: '20px',
                lineHeight: '1.6',
                flex: 1
              }}>
                Zobacz wszystkie funkcje systemu.
              </p>
              <a
                href="/funkcje"
                style={{
                  color: '#407EF9',
                  fontSize: '16px',
                  fontWeight: '500',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.gap = '8px';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.gap = '4px';
                }}
              >
                Funkcje →
              </a>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Case Study Section */}
      <div style={{ backgroundColor: '#f8f9fa', paddingTop: '60px', paddingBottom: '60px' }}>
        <Container className="py-5">
          <div className="text-center" style={{ marginBottom: '48px' }}>
            <h2 style={{
              fontSize: '2.2rem',
              fontWeight: '700',
              color: '#1f2937',
              marginBottom: '16px'
            }}>
              Case Studies
            </h2>
            <p style={{
              fontSize: '1.1rem',
              color: '#6b7280',
              maxWidth: '700px',
              margin: '0 auto'
            }}>
              Poznaj historie sukcesu naszych klientów
            </p>
          </div>

          <Row className="justify-content-center g-4">
            <Col lg={8} md={12}>
              <div
                style={{
                  backgroundColor: '#f8f9fa',
                  borderRadius: '20px',
                  padding: '40px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.1)';
                }}
                onClick={() => window.open('/case-study/staldach-bud', '_self')}
              >
                <Row className="align-items-center mb-4">
                  <Col md={4} className="text-center mb-4 mb-md-0">
                    <div style={{ marginBottom: '20px' }}>
                      <Image
                        src={require('../images/staldach-bud-logo.png')}
                        alt="Staldach-Bud Logo"
                        style={{
                          width: '150px',
                          height: 'auto',
                        }}
                      />
                    </div>
                    <div style={{ marginTop: '25px', fontSize: '14px' }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                        Mariusz Wolny
                      </div>
                      <div style={{ color: '#666', marginBottom: '3px' }}>
                        Prezes Zarządu
                      </div>
                      <div style={{ color: '#666' }}>
                        STALDACH-BUD sp. z o.o.
                      </div>
                    </div>
                  </Col>

                  <Col md={8}>
                    <h3 style={{
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      marginBottom: '15px',
                      color: '#1f2937',
                    }}>
                      Jak Staldach-bud sp. z o.o. oszczędza ponad 9 tysięcy złotych miesięcznie
                    </h3>
                    <p style={{
                      fontStyle: 'italic',
                      marginBottom: '20px',
                      color: '#333',
                      fontSize: '16px',
                      lineHeight: '1.5',
                    }}>
                      "Jako przedsiębiorca wiecznie przytłoczony ilością zadań i obowiązków, nie zdawałem sobie sprawy, że w tak łatwy sposób można poprawić działanie firmy"
                    </p>
                    <div style={{
                      fontSize: '2rem',
                      fontWeight: 'bold',
                      color: '#407EF9',
                      marginBottom: '10px',
                    }}>
                      <AnimatedCounter value={9180} suffix=" zł" />
                    </div>
                    <p style={{
                      fontSize: '14px',
                      margin: 0,
                      fontWeight: '500',
                      color: '#666',
                    }}>
                      Miesięczne oszczędności
                    </p>
                  </Col>
                </Row>

                <div className="mt-auto text-end">
                  <Button
                    variant="outline-dark"
                    style={{
                      borderRadius: '10px',
                      padding: '8px 20px',
                      fontWeight: '600'
                    }}
                  >
                    Przeczytaj case study
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* FAQ Section */}
      <div style={{ backgroundColor: '#f8f9fa' }}>
        <FAQ />
      </div>

      <Footer />
    </>
  );
}

export default Home;
